import { useState, useEffect, useMemo } from "react";
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State,
} from "@progress/kendo-data-query";
import { debounce } from "lodash";

interface UseReportsSearchOptions {
  debounceMs?: number;
  searchFields?: string[];
  onSearchChange?: () => void;
}

interface UseReportsSearchReturn {
  searchValue: string;
  searchFilter: CompositeFilterDescriptor | undefined;
  handleSearchChange: (value: string) => void;
}

const isFilterDescriptor = (
  filter: CompositeFilterDescriptor | FilterDescriptor
): filter is FilterDescriptor => {
  return "field" in filter && "operator" in filter;
};

const isCompositeFilter = (
  filter: CompositeFilterDescriptor | FilterDescriptor
): filter is CompositeFilterDescriptor => {
  return "logic" in filter && "filters" in filter;
};

export const useReportsSearch = ({
  debounceMs = 500,
  searchFields = ["name"],
  onSearchChange,
}: UseReportsSearchOptions): UseReportsSearchReturn => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        onSearchChange?.();
      }, debounceMs),
    [debounceMs, onSearchChange]
  );

  const handleSearchChange = (value: string) => {
    setSearchValue(value);

    debouncedSearch.cancel();

    if (!value || value.length === 0) {
      setDebouncedSearchValue("");
      onSearchChange?.();
    } else {
      debouncedSearch(value);
    }
  };

  // Generate search filter that can be merged with other filters
  const searchFilter = useMemo(() => {
    if (!debouncedSearchValue) {
      return undefined;
    }

    const searchFilters = searchFields.map((field) => ({
      field,
      operator: "contains",
      value: debouncedSearchValue,
    }));

    return {
      logic: "or",
      filters: searchFilters,
    } as CompositeFilterDescriptor;
  }, [debouncedSearchValue, searchFields]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return {
    searchValue,
    searchFilter,
    handleSearchChange,
  };
};
